import { Typography, TextField } from "@mui/material";
import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Coordinates from "../Shared/Coordinates";
import SymbolSelector from "./components/SymbolSelector";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { createOrUpdateShape } from "orion-components/GlobalData/Actions";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation, Translate } from "orion-components/i18n";
import MapDisplayOptions from "../Shared/MapDisplayOptions";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";

const CustomizedTextField = styled(TextField)({
	"& label": {
		color: "#9A9FA3",
		fontSize: 12
	},
	"& label.Mui-focused": {
		color: "#9A9FA3"
	},
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571"
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		}
	}
});

const PointEditor = () => {
	const dispatch = useDispatch();

	const mapTools = useSelector(state => state.mapState.mapTools);
	const dir = useSelector(state => getDir(state));

	const [pointName, setPointName] = useState("");
	const [description, setDescription] = useState("");
	const [frameName, setFrameName] = useState("");
	const [iconName, setIconName] = useState("");
	const [symbolCat, setSymbolCat] = useState("");

	const handleIconAndFrameChange = (icon, cat, frame) => {
		setIconName(icon);
		setSymbolCat(cat);
		setFrameName(frame);
	};

	useEffect(() => {
		const { feature } = mapTools;
		if (feature) {
			const { properties } = feature;
			const { name, description, symbol, symbolCategory } = properties;
			setSymbolCat(symbolCategory);
			if (symbolCategory === "Basic") {
				const parts = symbol.split("/");
				setFrameName(parts[1]);
				setIconName(parts[0]);
			} else {
				setIconName(symbol);
			}

			dispatch(createOrUpdateShape("point", { name, description, symbol }));

			setPointName(name);
			setDescription(description);
		}
	}, []);

	const styles = {
		icon: {
			marginTop: -70,
			width: 30,
			height: 30,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			...(dir === "rtl" ? { marginRight: 34.5 } : { marginLeft: 34.5 })
		}
	};
	return (
		<div>
			<div
				style={{
					marginTop: 24,
					...(dir === "ltr" && { marginLeft: 20, marginRight: 8 }),
					...(dir === "rtl" && { marginRight: 20, marginLeft: 8 })
				}}
			>
				<Typography
					sx={{
						fontSize: 14,
						fontFamily: "Roboto",
						letterSpacing: 0,
						color: "#FFFFFF",
						opacity: "1",
						textAlign: dir === "ltr" ? "left" : "right"
					}}
				>
					<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.header" />
				</Typography>
				<div style={{ marginTop: 16, display: "flex", justifyContent: "center", alignContent: "center" }}>
					{iconName === "" ? (
						<div style={{ position: "relative", display: "inline-block" }}>
							<img
								alt="dashed-react"
								src={require(`../icons/dashed-rect.png`)}
								style={{ width: 70, height: 70 }}
							/>
							<img
								alt="point_black"
								src={require(`../icons/icon-point.png`)}
								style={{
									position: "absolute",
									top: "40%",
									left: "50%",
									transform: "translate(-50%, -50%)",
									filter: "invert(30%) sepia(100%) saturate(0%) hue-rotate(0deg)",
									width: iconName === "" ? 25 : 62,
									height: iconName === "" ? "auto" : 62
								}}
							/>
						</div>
					) : symbolCat === "Basic" ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								width: 100,
								height: 100,
								marginTop: -10,
								marginRight: -25,
								marrginLeft: 20
							}}
						>
							<div>
								<SpriteIcon iconName={frameName} customWidth="100px" customHeight="100px" />
							</div>
							<div style={styles.icon}>
								<SpriteIcon
									iconName={iconName}
									customStyles={{ maxWidth: "100%", maxHeight: "100%" }}
								/>
							</div>
						</div>
					) : (
						<div style={{ width: 100, height: 100 }}>
							<SpriteIcon iconName={iconName} customWidth="65px" customHeight="62px" />
						</div>
					)}
					<div
						style={{
							...(dir === "ltr" && { marginLeft: 20 }),
							...(dir === "rtl" && { marginRight: 20 }),
							width: "100%",
							marginTop: "-10px"
						}}
					>
						<div
							style={{
								fontSize: 11,
								fontFamily: "Roboto",
								letterSpacing: 0,
								opacity: "1",
								marginBottom: 5.5,
								textAlign: dir === "ltr" ? "left" : "right",
								display: "flex"
							}}
						>
							<p
								style={{
									color: "#D3615C",
									width: 7,
									height: 13,
									...(dir === "ltr" && { marginRight: 5 }),
									...(dir === "rtl" && { marginLeft: 5 }),
									marginTop: "-4px"
								}}
							>
								&#9913;
							</p>
							<p
								style={{
									color: "#9A9FA3"
								}}
							>
								<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.name" />
							</p>
						</div>

						<CustomizedTextField
							placeholder={getTranslation(
								"global.sharedComponents.shapeEditor.components.pointEditor.pointName"
							)}
							variant="outlined"
							name="pointName"
							value={pointName}
							fullWidth
							onChange={e => {
								dispatch(createOrUpdateShape("point", { name: e.target.value }));
								setPointName(e.target.value);
							}}
							inputProps={{ style: { color: "#FFFFFF", fontSize: 12, fontFamily: "Roboto", height: 22 } }} data-testid="orion-components-shared-components-shape-editor-components-point-editor-point-editor-customized-text-field"
						/>
					</div>
				</div>
				<div style={{ marginTop: 16 }}>
					<p
						style={{
							fontSize: 11,
							fontFamily: "Roboto",
							letterSpacing: 0,
							color: "#9A9FA3",
							opacity: "1",
							textAlign: dir === "ltr" ? "left" : "right",
							...(dir === "ltr" && { marginLeft: 5.95 }),
							...(dir === "rtl" && { marginRight: 5.95 }),
							marginBottom: 5.5
						}}
					>
						<Translate value="global.sharedComponents.shapeEditor.components.pointEditor.description" />
					</p>
					<CustomizedTextField
						placeholder={getTranslation(
							"global.sharedComponents.shapeEditor.components.pointEditor.descriptionHere"
						)}
						variant="outlined"
						name="description"
						value={description}
						onChange={e => {
							setDescription(e.target.value);
							dispatch(createOrUpdateShape("point", { description: e.target.value }));
						}}
						multiline={true}
						fullWidth
						rows={4}
						inputProps={{ style: { color: "#FFFFFF", fontSize: 12, fontFamily: "Roboto" } }} data-testid="orion-components-shared-components-shape-editor-components-point-editor-point-editor-customized-text-field-2"
					/>
				</div>
			</div>

			<Coordinates />

			<MapDisplayOptions shapeType="point" />
			<SymbolSelector
				handleIconAndFrameChange={handleIconAndFrameChange}
				iconName={iconName}
				defaultColor={frameName?.replace("frame_circle_map_", "")}
			/>
		</div>
	);
};
export default PointEditor;
